import BASE_URL from "../config";

const CACHE_TTL_MS = 60 * 1000; // short, so an admin hiding a category shows up quickly
const CACHE_KEY = "astro_categories_cache_v1";

let cachedResult = null;
let cachedAt = 0;
let inFlightPromise = null;

const readStorage = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.timestamp !== "number") return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;

    return parsed.data;
  } catch {
    return null;
  }
};

const writeStorage = (data) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {
    // localStorage unavailable (private mode/quota) - memory cache still works
  }
};

// Every homepage section independently wants the full category list just to
// find its own one category by slug. This dedupes concurrent callers into a
// single request and caches the result (memory + localStorage) so repeat
// mounts and page reloads within CACHE_TTL_MS don't hit the network again.
export const getCategories = async ({ force = false } = {}) => {
  if (!force) {
    if (cachedResult && Date.now() - cachedAt < CACHE_TTL_MS) {
      return cachedResult;
    }

    if (inFlightPromise) {
      return inFlightPromise;
    }

    const stored = readStorage();
    if (stored) {
      cachedResult = stored;
      cachedAt = Date.now();
      return stored;
    }
  }

  inFlightPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/categories`);

      if (!response.ok) {
        throw new Error(`Categories API failed: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success) {
        cachedResult = result;
        cachedAt = Date.now();
        writeStorage(result);
      }

      return result;
    } catch (error) {
      console.error("getCategories Error:", error);
      throw error;
    } finally {
      inFlightPromise = null;
    }
  })();

  return inFlightPromise;
};
